import React from 'react';
import Modal from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import Spinner from '../../../components/ui/Spinner';
import { MarkdownRenderer } from './markdown/MarkdownParser';

interface AnalysisModalProps {
    isOpen: boolean;
    onClose: () => void;
    isLoading: boolean;
    analysisResult: string;
    analysisError?: string | null;
    onRetry?: () => void;
}

const AnalysisModal: React.FC<AnalysisModalProps> = ({
    isOpen,
    onClose,
    isLoading,
    analysisResult,
    analysisError,
    onRetry,
}) => {
    return (
        <Modal isOpen={isOpen} onClose={onClose} size="2xl">
            <Modal.Header onClose={onClose}>
                Análise de Vendas IA
            </Modal.Header>

            <Modal.Body>
                <div className="flex flex-col gap-s5 sm:gap-s6">
                    <p className="text-pretty text-sm leading-relaxed text-muted">
                        Insights gerados com base nos seus clientes potenciais recentes.
                    </p>

                    {isLoading ? (
                        <LoadingState />
                    ) : analysisError ? (
                        <ErrorState message={analysisError} onRetry={onRetry} />
                    ) : (
                        <AnalysisContent result={analysisResult} />
                    )}
                </div>
            </Modal.Body>

            <Modal.Footer>
                <div className="flex w-full flex-col gap-s3 sm:flex-row sm:items-center sm:justify-between">
                    <p className="order-2 text-center text-sm font-normal leading-relaxed text-muted sm:order-1 sm:text-left">
                        Gerado por Adlitics AI · A precisão pode variar
                    </p>
                    <Button
                        onClick={onClose}
                        variant="secondary"
                        className="order-1 min-h-11 w-full touch-manipulation sm:order-2 sm:w-auto"
                    >
                        Fechar
                    </Button>
                </div>
            </Modal.Footer>
        </Modal>
    );
};

const LoadingState: React.FC = () => (
    <div
        className="flex flex-col items-center justify-center px-s4 py-s12 text-center"
        role="status"
        aria-live="polite"
    >
        <div className="rounded-full bg-primary-subtle p-s4" aria-hidden>
            <Spinner size="lg" />
        </div>
        <h3 className="mt-s5 text-base font-semibold text-default">
            Analisando seus dados…
        </h3>
        <p className="mx-auto mt-s2 max-w-xs text-pretty text-sm leading-relaxed text-muted">
            A IA está lendo desempenho de clientes potenciais, origens e agentes.
        </p>
    </div>
);

const ErrorState: React.FC<{ message: string; onRetry?: () => void }> = ({
    message,
    onRetry,
}) => (
    <div
        className="rounded-xl border border-error/30 bg-danger-bg px-s4 py-s10 text-center"
        role="alert"
        aria-live="assertive"
    >
        <p className="text-sm font-medium text-default">Falha ao gerar a análise</p>
        <p className="mx-auto mt-s2 max-w-md text-pretty text-sm leading-relaxed text-muted">
            {message}
        </p>
        {onRetry ? (
            <Button
                onClick={onRetry}
                variant="secondary"
                className="mt-s5 min-h-11 touch-manipulation"
            >
                Tentar novamente
            </Button>
        ) : null}
    </div>
);

const AnalysisContent: React.FC<{ result: string }> = ({ result }) => {
    if (!result) {
        return (
            <div className="rounded-xl border border-dashed border-subtle bg-surface-1 px-s4 py-s10 text-center">
                <p className="text-sm font-medium text-default">
                    Nenhuma análise disponível no momento.
                </p>
                <p className="mt-s2 text-sm text-muted">
                    Tente novamente com outro período ou mais dados no dashboard.
                </p>
            </div>
        );
    }

    return (
        <div className="border-t border-subtle pt-s5">
            <MarkdownRenderer content={result} />
        </div>
    );
};

export default AnalysisModal;
